<script setup lang="ts">
import { ref } from "vue";
import { Permission } from "../../../../../types/auth";
import { CashierDuty, PosStation, RecordStatus } from "../../../../../types/models";
import { FilterField, TableAction, TableField } from "../../../../../components/table/table";
import ListTemplate from "../../../../../components/ui/common/ListTemplate.vue";
import ModalFormTemplate from '../../../../../components/ui/Templates/ModalFormTemplate.vue'
import { api } from "../../../../../api/Api";
import MessageBox from "../../../../../services/MessageBox";
import { printPDFBlob } from "../../../../../utils/CommonFunctions";

const props = defineProps<{
  posStation?: PosStation | undefined
  cashierDuty?: CashierDuty | null
}>();

const endPoint = "revenue/sales/pos/";
const title = "Sales Invoices";
const selectedData = ref<any | null>(null);
const showForm = ref(false);
const formMode = ref<
  "create" | "edit" | "view" | "showQuickView" | "toggleStatus"
>("view");
const refreshData = ref(false);
const showReceivePayment = ref(false)
const permissions = ref<Permission[]>();

const fields: TableField[] = [
  { name: "date_time", caption: "Date", sortable: true, fieldType: "date" },
  { name: "due_date", caption: "Due Date", sortable: true, fieldType: "date" },
  {
    name: "document_no",
    caption: "Ref. No",
    sortable: true,
    fieldType: "text",
  },
  {
    name: "warehouse",
    caption: "Warehouse",
    sortable: true,
    fieldType: "text",
  },
  { name: "outlet", caption: "Outlet", sortable: true, fieldType: "text" },
  { name: "customer", caption: "customer", sortable: true, fieldType: "text" },
  { name: "net_total", caption: "Amount", sortable: true, fieldType: "number" },
  {
    name: "due_amount",
    caption: "Due Amount",
    sortable: true,
    fieldType: "number",
  },
  {
    name: "sales_channel",
    caption: "Channel",
    sortable: true,
    fieldType: "text",
  },
  {
    name: "delivery_date",
    caption: "Delivery By",
    sortable: true,
    fieldType: "date",
  },
  {
    name: "credit_term",
    caption: "Payment Terms",
    sortable: true,
    fieldType: "text",
  },
  {
    name: "sales_person",
    caption: "Sales Rep",
    sortable: true,
    fieldType: "text",
  },
  {
    name: "customer_reference",
    caption: "customer Reference",
    sortable: true,
    fieldType: "text",
  },
  {
    name: "to_deliver",
    caption: "To Deliver",
    sortable: false,
    fieldType: "boolean",
  },
  {
    name: "record_status",
    caption: "Status",
    sortable: false,
    fieldType: "text",
  },
];
const actions: TableAction[] = [
  {
    name: "create-debit-note",
    icon: "WalletCards",
    variant: "red",
    title: "Create Credit Note",
    permission: "Create Credit Note",
    ignorePermission: false,
    visible: (item) => item.record_status == 'Approved',
  },
  {
    name: "print-thermal",
    icon: "Printer",
    variant: "primary",
    title: "Print Thermal Invoice",
    permission: "",
    ignorePermission: true,
    visible: () => true,
  },
];

const handleConfirmCreditNote = (item: any) => {
  const message = `Are you sure you want to create a Credit Note for this invoice?`;
  MessageBox.confirm({
    title: "Create Credt Note",
    message: message,
    confirmText: "Create",
    cancelText: "Cancel",
  }).then((confirmed) => {
    if (confirmed) {
      handleCreateCreditNote(item);
    }
  });
};

const handleCreateCreditNote = async (item: any) => {
  const response = await api.post(
    (endPoint.slice(-1) == "/" ? endPoint : endPoint + "/") +
      "credit-note",
    {
      id: item.uid,
      convert: 'Credit Note',
    }
  );
  if (response.success == true) {
    refreshData.value = !refreshData.value;
    MessageBox.confirm({
      title: "Credit Note Created",
      message:
        "Credit Note created successfully",
      confirmText: "Print Credit Note",
      cancelText: "Close",
    }).then((confirmed) => {
      if (confirmed) {
        printPDFBlob(response.data);
      }
    });
  }
};

const handlePrintThermal = async (item: any) => {
  const response = await api.post(
    (endPoint.slice(-1) == "/" ? endPoint : endPoint + "/") +
      "print-thermal",
    {
      id: item.uid,
      warehouses_id: props.posStation?.outlet?.warehouses_id ?? "",
      outlets_id: props.posStation?.outlet?.id ?? "",
      outlet_pos_stations_id: props.posStation?.id ?? "",
      price_lists_id: props.posStation?.outlet?.price_lists_id ?? "",
      sales_channels_id: props.posStation?.outlet?.sales_channels_id ?? "",
      thermal: true,
      cashier_dutys_id: props.cashierDuty ? props.cashierDuty.id : "",
      left_margin: props.posStation?.left_margin ?? 0,
      right_margin: props.posStation?.right_margin ?? 0,
      top_margin: props.posStation?.top_margin ?? 0,
      button_margin: props.posStation?.button_margin ?? 0,
      font_size: props.posStation?.font_size ?? 0,
      font_bold: props.posStation?.font_bold ?? false,
      recepit_size: props.posStation?.recepit_size ?? 80,
      print_barcode: props.posStation?.print_barcode ?? true,
    }
  );
  if (response.success == true) {
    printPDFBlob(response.data);
  }
};

const filterFields: FilterField[] = [
  {
    name: "active",
    label: "Status",
    type: "select",
    value: "",
    options: [
      { id: "1", name: "Active" },
      { id: "0", name: "Inactive" },
    ],
  },
];

const changeMode = (
  mode: "create" | "edit" | "view" | "showQuickView" | "toggleStatus"
) => {
  console.log('changeMode', mode)
  formMode.value = mode;
  showForm.value = mode === "view" || mode === "edit" || mode === "create";
};

const handleData = (data: any) => {
  selectedData.value = data;
};

const record_status = ref<RecordStatus[]>([]);

const handleLists = (list: any) => {
  record_status.value = list["record_status"];
};

const handleTableActions = (action: any, data: any)  => {
  selectedData.value = data
  if (action == 'receive-payment') {
    showReceivePayment.value = true
  } else if (action == 'create-debit-note') {
    handleConfirmCreditNote(data)
  } else if (action == 'print-thermal') {
    handlePrintThermal(data)
  }
}

const handlePermissions = (perm: Permission[]) => {
  permissions.value = perm;
};

const listViewRef = ref<InstanceType<typeof ListTemplate> | null>(null);
/*const viewDocument = (data : any, title: string, path: string) => {
  if (listViewRef.value) {
    listViewRef.value.viewDocument(data, title, path);
  }
}*/

</script>

<template>
    <ModalFormTemplate
    mode="view" title="Sales Invoices" :endPoint="endPoint" :fullscreen="true"    
    :tabList="[]" 
    @close="$emit('close')"
    >
    <template #form>
  <ListTemplate
   ref="listViewRef"
    endPoint="/clients/revenue/sales/sales-invoices/"
    :ignore-max-height="true"
    :title="title"
    :fields="fields"
    :actions="actions"
    :show-header="false"
    :filterFields="filterFields"
    @permissions="handlePermissions"
    :tabList="[]"
    :refreshData="refreshData"
    @custom="handleTableActions"
    @mode="changeMode"
    @show="showForm = true"
    :showForm="showForm"
    @lists="handleLists"
    :allow-disable="false"
    :show-quick-view="true"
    :show-share-actions="true"
    :allow-edit="false"
    @data="handleData"
  >
  </ListTemplate>
  </template>
  </ModalFormTemplate>


</template>
